import React, { useEffect, useMemo, useState } from 'react';
import { Download, FileSpreadsheet, Calendar, Building2, MapPin, AlertCircle, Search, TrendingUp, DollarSign, PieChart as PieChartIcon, BarChart2, Clock, CalendarDays, Wallet, Tag } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { DatePicker } from '../components/ui/DatePicker';
import { useOrders } from '../contexts/OrderContext';
import { useToast } from '../components/ui/Toast';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { exportSalesReport } from '../utils/excelExport';
import { cleanProductName } from '../utils/formatters';
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
export function ReportsPage() {
  useDocumentTitle('Reportes');
  const {
    orders
  } = useOrders();
  const {
    showToast
  } = useToast();
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('Todas');
  const [selectedBranch, setSelectedBranch] = useState('Todas');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateError, setDateError] = useState('');
  // Applied filters - start showing ALL orders by default
  const [appliedFilters, setAppliedFilters] = useState({
    company: 'Todas',
    branch: 'Todas',
    dateFrom: '',
    dateTo: ''
  });
  // Load companies dynamically from Supabase
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        if (isSupabaseConfigured()) {
          const data = await supabaseClient.getCompanies();
          setCompanies(data);
        } else {
          const saved = localStorage.getItem('criket_companies');
          setCompanies(saved ? JSON.parse(saved) : []);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
        const saved = localStorage.getItem('criket_companies');
        setCompanies(saved ? JSON.parse(saved) : []);
      }
    };
    loadCompanies();
  }, []);
  const companyNames = ['Todas', ...companies.map((c: any) => c.name)];
  const branches = selectedCompany === 'Todas' ? ['Todas'] : ['Todas', ...(companies.find((c: any) => c.name === selectedCompany)?.branches?.map((b: any) => b.name) || [])];
  // Validate date range
  const validateDates = (from: string, to: string) => {
    if (!from || !to) {
      setDateError('');
      return true;
    }
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (fromDate > toDate) {
      setDateError('La fecha "Desde" no puede ser posterior a la fecha "Hasta"');
      return false;
    }
    setDateError('');
    return true;
  };
  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    validateDates(value, dateTo);
  };
  const handleDateToChange = (value: string) => {
    setDateTo(value);
    validateDates(dateFrom, value);
  };
  const handleSearch = () => {
    if (dateError) {
      return;
    }
    setAppliedFilters({
      company: selectedCompany,
      branch: selectedBranch,
      dateFrom: dateFrom,
      dateTo: dateTo
    });
  };
  const filteredOrders = orders.filter(order => {
    // Don't filter cancelled orders
    if (order.status === 'Cancelado') {
      return false;
    }
    // Apply company filter
    if (appliedFilters.company !== 'Todas' && order.company !== appliedFilters.company) {
      return false;
    }
    // Apply branch filter
    if (appliedFilters.branch !== 'Todas' && order.branch !== appliedFilters.branch) {
      return false;
    }
    // Apply date filtering ONLY if dates are provided
    if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      try {
        // Parse order date from Spanish format "DD/MM/YYYY HH:MM:SS AM/PM" or "DD/MM/YYYY, HH:MM:SS AM/PM"
        const cleanDate = order.orderDate.replace(',', '');
        const dateTimeParts = cleanDate.split(' ');
        const datePart = dateTimeParts[0]; // "DD/MM/YYYY"
        const parts = datePart.split('/');
        if (parts.length !== 3) {
          return true;
        }
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        // Create date string in YYYY-MM-DD format for comparison (no timezone issues)
        const orderDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        if (appliedFilters.dateFrom) {
          if (orderDateStr < appliedFilters.dateFrom) {
            return false;
          }
        }
        if (appliedFilters.dateTo) {
          if (orderDateStr > appliedFilters.dateTo) {
            return false;
          }
        }
      } catch (e) {
        console.error('❌ Error parsing date:', order.orderDate, e);
        return true;
      }
    }
    return true;
  });
  // Calculate totals
  const totalSales = filteredOrders.reduce((sum, order) => sum + parseFloat(order.total), 0);
  const totalDiscount = filteredOrders.reduce((sum, order) => sum + parseFloat(order.discount || '0'), 0);
  const totalSubsidy = filteredOrders.reduce((sum, order) => sum + parseFloat(order.subsidyAmount || '0'), 0);
  const exemptSales = filteredOrders.filter(o => o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
  const taxableSales = filteredOrders.filter(o => !o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
  const subtotalExempt = exemptSales;
  const subtotalTaxable = taxableSales / 1.18;
  const subtotal = subtotalExempt + subtotalTaxable;
  const tax = taxableSales - subtotalTaxable;
  const totalOrders = filteredOrders.length;
  const averageOrder = totalOrders > 0 ? totalSales / totalOrders : 0;
  const salesByCompany = companies.map(company => {
    const companyOrders = filteredOrders.filter(o => o.company === company.name);
    const total = companyOrders.reduce((sum, o) => sum + parseFloat(o.total), 0);
    const exemptTotal = companyOrders.filter(o => o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
    const taxableTotal = companyOrders.filter(o => !o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
    return {
      company: company.name,
      orders: companyOrders.length,
      total: total,
      subtotal: exemptTotal + taxableTotal / 1.18,
      tax: taxableTotal - taxableTotal / 1.18,
      exempt: exemptTotal,
      taxable: taxableTotal
    };
  }).filter(s => s.orders > 0).sort((a, b) => b.total - a.total); // Sort by total sales descending
  const salesByBranch = filteredOrders.reduce((acc, order) => {
    const key = `${order.company} - ${order.branch}`;
    if (!acc[key]) {
      acc[key] = {
        company: order.company,
        branch: order.branch,
        orders: 0,
        total: 0,
        subtotal: 0,
        tax: 0,
        exempt: 0,
        taxable: 0
      };
    }
    const orderTotal = parseFloat(order.total);
    acc[key].orders++;
    acc[key].total += orderTotal;
    if (order.taxExempt) {
      acc[key].exempt += orderTotal;
      acc[key].subtotal += orderTotal;
    } else {
      acc[key].taxable += orderTotal;
      acc[key].subtotal += orderTotal / 1.18;
      acc[key].tax += orderTotal - orderTotal / 1.18;
    }
    return acc;
  }, {} as Record<string, any>);
  const branchSales = Object.values(salesByBranch).sort((a: any, b: any) => b.total - a.total);
  // --- CHART DATA PREPARATION ---
  // 1. Revenue Trend Data (AreaChart)
  const revenueTrendData = useMemo(() => {
    const dataMap: Record<string, {
      date: string;
      ventas: number;
      subsidios: number;
    }> = {};
    filteredOrders.forEach(order => {
      try {
        const cleanDate = order.orderDate.replace(',', '');
        const datePart = cleanDate.split(' ')[0]; // 'DD/MM/YYYY'
        const [day, month, year] = datePart.split('/');
        // Format for sorting/display: YYYY-MM-DD
        const dateKey = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        // Display format: DD/MM
        const displayDate = `${day}/${month}`;
        if (!dataMap[dateKey]) {
          dataMap[dateKey] = {
            date: displayDate,
            ventas: 0,
            subsidios: 0
          };
        }
        dataMap[dateKey].ventas += parseFloat(order.total);
        dataMap[dateKey].subsidios += parseFloat(order.subsidyAmount || '0');
      } catch (e) {
        // ignore parsing errors
      }
    });
    // Sort by date key and return array
    return Object.keys(dataMap).sort().map(key => dataMap[key]);
  }, [filteredOrders]);
  // 2. Top Products Data (PieChart)
  const topProductsData = useMemo(() => {
    const productMap: Record<string, number> = {};
    filteredOrders.forEach(order => {
      // Clean product name: remove "1x " prefix and strip selections in parentheses
      let product = cleanProductName(order.product || 'Desconocido');
      // Remove everything in parentheses to show only the base dish name
      product = product.replace(/\s*\([^)]*\)/g, '').trim();
      if (!product) product = 'Desconocido';
      if (!productMap[product]) {
        productMap[product] = 0;
      }
      productMap[product] += parseFloat(order.total);
    });
    return Object.entries(productMap).map(([name, value]) => ({
      name,
      value
    })).sort((a, b) => b.value - a.value).slice(0, 6); // Top 6 products
  }, [filteredOrders]);
  // 3. Sales by Company Data (BarChart)
  const companyChartData = useMemo(() => {
    return salesByCompany.slice(0, 10).map(c => ({
      name: c.company,
      ventas: c.total
    }));
  }, [salesByCompany]);
  // 4. Orders by Hour Data (BarChart)
  const ordersByHourData = useMemo(() => {
    // Initialize hours 6 to 23 (6am to 11pm)
    const hourCounts = Array.from({
      length: 18
    }, (_, i) => {
      const h = i + 6;
      return {
        hour: h,
        label: `${h % 12 || 12} ${h >= 12 ? 'PM' : 'AM'}`,
        pedidos: 0
      };
    });
    filteredOrders.forEach(order => {
      try {
        let h = -1;
        // Match time pattern HH:MM:SS
        const timeMatch = order.orderDate.match(/(\d{1,2}):(\d{2}):(\d{2})/);
        if (timeMatch) {
          h = parseInt(timeMatch[1], 10);
          const isPM = order.orderDate.toLowerCase().includes('p');
          const isAM = order.orderDate.toLowerCase().includes('a');
          if (isPM && h < 12) h += 12;
          if (isAM && h === 12) h = 0;
        }
        if (h >= 6 && h <= 23) {
          const hourEntry = hourCounts.find(entry => entry.hour === h);
          if (hourEntry) {
            hourEntry.pedidos += 1;
          }
        }
      } catch (e) {
        // ignore
      }
    });
    return hourCounts;
  }, [filteredOrders]);
  const peakHour = useMemo(() => {
    if (ordersByHourData.length === 0) return null;
    return ordersByHourData.reduce((max, current) => current.pedidos > max.pedidos ? current : max, ordersByHourData[0]);
  }, [ordersByHourData]);
  // 5. Orders by Day of Week Data (BarChart)
  const ordersByDayData = useMemo(() => {
    const days = [{
      day: 1,
      label: 'Lun',
      pedidos: 0
    }, {
      day: 2,
      label: 'Mar',
      pedidos: 0
    }, {
      day: 3,
      label: 'Mié',
      pedidos: 0
    }, {
      day: 4,
      label: 'Jue',
      pedidos: 0
    }, {
      day: 5,
      label: 'Vie',
      pedidos: 0
    }, {
      day: 6,
      label: 'Sáb',
      pedidos: 0
    }, {
      day: 0,
      label: 'Dom',
      pedidos: 0
    }];
    filteredOrders.forEach(order => {
      try {
        const datePart = order.orderDate.split(',')[0].split(' ')[0]; // "DD/MM/YYYY"
        const [d, m, y] = datePart.split('/');
        // Month is 0-indexed in JS Date
        const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        const dayOfWeek = date.getDay();
        const dayEntry = days.find(entry => entry.day === dayOfWeek);
        if (dayEntry) {
          dayEntry.pedidos += 1;
        }
      } catch (e) {
        // ignore
      }
    });
    return days;
  }, [filteredOrders]);
  const exportToExcel = () => {
    if (dateError) {
      showToast('warning', 'Error en fechas', 'Corrige el rango de fechas antes de exportar');
      return;
    }
    const orderDetails = filteredOrders.map(order => {
      const orderTotal = parseFloat(order.total);
      const orderSubtotal = order.taxExempt ? orderTotal : orderTotal / 1.18;
      const orderTax = order.taxExempt ? 0 : orderTotal - orderTotal / 1.18;
      const orderSubsidy = parseFloat(order.subsidyAmount || '0');
      return {
        orderNo: order.orderNo,
        orderDate: order.orderDate,
        name: order.name,
        company: order.company,
        branch: order.branch,
        product: order.product,
        quantity: order.quantity,
        subtotal: orderSubtotal,
        tax: orderTax,
        discount: parseFloat(order.discount || '0'),
        subsidy: orderSubsidy,
        taxExempt: order.taxExempt || false,
        total: orderTotal,
        status: order.status
      };
    });
    exportSalesReport({
      filters: {
        company: appliedFilters.company,
        branch: appliedFilters.branch,
        dateFrom: appliedFilters.dateFrom,
        dateTo: appliedFilters.dateTo
      },
      summary: {
        totalOrders,
        subtotal,
        tax,
        totalDiscount,
        totalSubsidy,
        exemptSales,
        taxableSales,
        totalSales,
        averageOrder
      },
      salesByCompany,
      salesByBranch: branchSales.map((s: any) => ({
        company: s.company,
        branch: s.branch,
        orders: s.orders,
        subtotal: s.subtotal,
        tax: s.tax,
        exempt: s.exempt,
        taxable: s.taxable,
        total: s.total
      })),
      orderDetails
    });
    showToast('success', 'Reporte exportado', 'El archivo Excel se ha descargado correctamente');
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white dark:bg-gray-800 p-3 border border-gray-100 dark:border-gray-700 shadow-lg rounded-xl" data-id="element-5504">
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-2" data-id="element-5505">
            {label}
          </p>
          {payload.map((entry: any, index: number) => <p key={index} className="text-xs" style={{
          color: entry.color || entry.fill
        }} data-id="element-5506">
              {entry.name}:{' '}
              {entry.name === 'Pedidos' ? entry.value : `RD$ ${entry.value.toLocaleString('es-DO', {
            minimumFractionDigits: 2
          })}`}
            </p>)}
        </div>;
    }
    return null;
  };
  return <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-id="element-5507">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5508">
        <div data-id="element-5509">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight" data-id="element-5510">
            Reportes de Ventas
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-lg" data-id="element-5511">
            Analiza y exporta datos de ventas por empresa y sucursal
          </p>
        </div>
        <Button onClick={exportToExcel} leftIcon={<Download size={18} data-id="element-5513" />} size="lg" disabled={filteredOrders.length === 0 || !!dateError} className="shadow-lg shadow-gray-900/10" data-id="element-5512">
          Exportar a Excel
        </Button>
      </div>
      {/* Filters */}
      <Card className="p-0 border-0 shadow-lg shadow-gray-100 dark:shadow-none" data-id="element-5514">
        <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800" data-id="element-5515">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5516">
            <Search size={16} className="text-gray-400" data-id="element-5517" />
            Filtros de Búsqueda
          </h3>
        </div>

        <div className="p-6" data-id="element-5518">
          {dateError && <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3" data-id="element-5519">
              <AlertCircle className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" size={18} data-id="element-5520" />
              <div data-id="element-5521">
                <p className="text-sm font-bold text-red-900 dark:text-red-100" data-id="element-5522">
                  Error en las fechas
                </p>
                <p className="text-xs text-red-700 dark:text-red-300 mt-0.5" data-id="element-5523">
                  {dateError}
                </p>
              </div>
            </div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end" data-id="element-5524">
            <div data-id="element-5525">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider" data-id="element-5526">
                Empresa
              </label>
              <FilterDropdown value={selectedCompany} options={companyNames} onChange={setSelectedCompany} data-id="element-5527" />
            </div>
            <div data-id="element-5528">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider" data-id="element-5529">
                Sucursal
              </label>
              <FilterDropdown value={selectedBranch} options={branches} onChange={setSelectedBranch} data-id="element-5530" />
            </div>
            <div data-id="element-5531">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider" data-id="element-5532">
                Desde
              </label>
              <DatePicker value={dateFrom} onChange={handleDateFromChange} placeholder="Fecha inicio" data-id="element-5533" />
            </div>
            <div data-id="element-5534">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider" data-id="element-5535">
                Hasta
              </label>
              <DatePicker value={dateTo} onChange={handleDateToChange} placeholder="Fecha fin" data-id="element-5536" />
            </div>
            <div data-id="element-5537">
              <Button onClick={handleSearch} leftIcon={<Search size={16} data-id="element-5539" />} disabled={!!dateError} fullWidth className="h-10" data-id="element-5538">
                Aplicar Filtros
              </Button>
            </div>
          </div>

          {/* Active filters indicator */}
          {(appliedFilters.company !== 'Todas' || appliedFilters.branch !== 'Todas' || appliedFilters.dateFrom || appliedFilters.dateTo) && <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2" data-id="element-5540">
              <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider" data-id="element-5541">
                Filtros activos:
              </span>
              {appliedFilters.company !== 'Todas' && <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md text-xs font-medium" data-id="element-5542">
                  <Building2 size={12} data-id="element-5543" /> {appliedFilters.company}
                </span>}
              {appliedFilters.branch !== 'Todas' && <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md text-xs font-medium" data-id="element-5544">
                  <MapPin size={12} data-id="element-5545" /> {appliedFilters.branch}
                </span>}
              {appliedFilters.dateFrom && <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md text-xs font-medium" data-id="element-5546">
                  <Calendar size={12} data-id="element-5547" /> Desde: {appliedFilters.dateFrom}
                </span>}
              {appliedFilters.dateTo && <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md text-xs font-medium" data-id="element-5548">
                  <Calendar size={12} data-id="element-5549" /> Hasta: {appliedFilters.dateTo}
                </span>}
              <button onClick={() => {
            setSelectedCompany('Todas');
            setSelectedBranch('Todas');
            setDateFrom('');
            setDateTo('');
            setDateError('');
            setAppliedFilters({
              company: 'Todas',
              branch: 'Todas',
              dateFrom: '',
              dateTo: ''
            });
          }} className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 font-medium underline underline-offset-2 transition-colors ml-1" data-id="element-5550">
                Limpiar todo
              </button>
            </div>}
        </div>
      </Card>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5551">
        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-blue-500 group hover:shadow-md transition-shadow" data-id="element-5552">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5553">
            <FileSpreadsheet className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-5554" />
          </div>
          <div data-id="element-5555">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5556">
              Total Pedidos
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5557">
              {totalOrders}
            </p>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-green-500 group hover:shadow-md transition-shadow" data-id="element-5558">
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5559">
            <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-5560" />
          </div>
          <div data-id="element-5561">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5562">
              Ventas Totales
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5563">
              RD${' '}
              {totalSales.toLocaleString('es-DO', {
              minimumFractionDigits: 2
            })}
            </p>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-orange-500 group hover:shadow-md transition-shadow" data-id="element-5564">
          <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5565">
            <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400" data-id="element-5566" />
          </div>
          <div data-id="element-5567">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5568">
              Ticket Promedio
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5569">
              RD${' '}
              {averageOrder.toLocaleString('es-DO', {
              minimumFractionDigits: 2
            })}
            </p>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-teal-500 group hover:shadow-md transition-shadow" data-id="element-5570">
          <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5571">
            <Wallet className="w-6 h-6 text-teal-600 dark:text-teal-400" data-id="element-5572" />
          </div>
          <div data-id="element-5573">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5574">
              Total Subsidios
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5575">
              RD${' '}
              {totalSubsidy.toLocaleString('es-DO', {
              minimumFractionDigits: 2
            })}
            </p>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-rose-500 group hover:shadow-md transition-shadow" data-id="element-5576">
          <div className="p-3 bg-rose-50 dark:bg-rose-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5577">
            <Tag className="w-6 h-6 text-rose-600 dark:text-rose-400" data-id="element-5578" />
          </div>
          <div data-id="element-5579">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5580">
              Total Descuentos
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5581">
              RD${' '}
              {totalDiscount.toLocaleString('es-DO', {
              minimumFractionDigits: 2
            })}
            </p>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-purple-500 group hover:shadow-md transition-shadow" data-id="element-5582">
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5583">
            <PieChartIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-5584" />
          </div>
          <div data-id="element-5585">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5586">
              ITBIS (18%)
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5587">
              RD${' '}
              {tax.toLocaleString('es-DO', {
              minimumFractionDigits: 2
            })}
            </p>
          </div>
        </Card>
      </div>
      {/* CHARTS SECTION */}
      <div className="space-y-6" data-id="element-5588">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200 dark:border-gray-800" data-id="element-5589">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg" data-id="element-5590">
            <BarChart2 size={20} className="text-indigo-600 dark:text-indigo-400" data-id="element-5591" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5592">
            Análisis Visual
          </h2>
        </div>

        {/* Revenue Trend Chart */}
        <Card className="p-6 border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-5593">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5594">
            Tendencia de Ingresos
          </h3>
          <div className="h-[300px] w-full" data-id="element-5595">
            <ResponsiveContainer width="100%" height="100%" data-id="element-5596">
              <AreaChart data={revenueTrendData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-5597">
                <defs data-id="element-5598">
                  <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1" data-id="element-5599">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} data-id="element-5600" />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-5601" />
                  </linearGradient>
                  <linearGradient id="colorSubsidios" x1="0" y1="0" x2="0" y2="1" data-id="element-5602">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} data-id="element-5603" />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-5604" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-5605" />
                <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} data-id="element-5606" />
                <YAxis stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `RD$${value / 1000}k`} data-id="element-5607" />
                <Tooltip content={<CustomTooltip data-id="element-5609" />} data-id="element-5608" />
                <Area type="monotone" dataKey="ventas" name="Ventas Totales" stroke="#3b82f6" fillOpacity={1} fill="url(#colorVentas)" strokeWidth={2} data-id="element-5610" />
                <Area type="monotone" dataKey="subsidios" name="Subsidios" stroke="#10b981" fillOpacity={1} fill="url(#colorSubsidios)" strokeWidth={2} data-id="element-5611" />
                <Legend data-id="element-5612" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* New Charts: Orders by Hour and Day */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5613">
          {/* Orders by Hour Chart */}
          <Card className="p-6 border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-5614">
            <div className="flex items-center gap-2 mb-6" data-id="element-5615">
              <Clock className="w-5 h-5 text-indigo-500" data-id="element-5616" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5617">
                Pedidos por Hora del Día
              </h3>
            </div>
            <div className="h-[250px] w-full" data-id="element-5618">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5619">
                <BarChart data={ordersByHourData} margin={{
                top: 5,
                right: 10,
                left: -20,
                bottom: 5
              }} data-id="element-5620">
                  <defs data-id="element-5621">
                    <linearGradient id="colorHour" x1="0" y1="0" x2="0" y2="1" data-id="element-5622">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} data-id="element-5623" />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0.2} data-id="element-5624" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-5625" />
                  <XAxis dataKey="label" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} data-id="element-5626" />
                  <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-5627" />
                  <Tooltip content={<CustomTooltip data-id="element-5629" />} cursor={{
                  fill: 'transparent'
                }} data-id="element-5628" />
                  <Bar dataKey="pedidos" name="Pedidos" fill="url(#colorHour)" radius={[4, 4, 0, 0]} data-id="element-5630" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {peakHour && peakHour.pedidos > 0 && <div className="mt-4 text-center" data-id="element-5631">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 px-3 py-1.5 rounded-full" data-id="element-5632">
                  Hora pico:{' '}
                  <strong className="text-indigo-600 dark:text-indigo-400" data-id="element-5633">
                    {peakHour.label}
                  </strong>{' '}
                  con {peakHour.pedidos} pedidos
                </span>
              </div>}
          </Card>

          {/* Orders by Day Chart */}
          <Card className="p-6 border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-5634">
            <div className="flex items-center gap-2 mb-6" data-id="element-5635">
              <CalendarDays className="w-5 h-5 text-emerald-500" data-id="element-5636" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5637">
                Pedidos por Día de la Semana
              </h3>
            </div>
            <div className="h-[250px] w-full" data-id="element-5638">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5639">
                <BarChart data={ordersByDayData} margin={{
                top: 5,
                right: 10,
                left: -20,
                bottom: 5
              }} data-id="element-5640">
                  <defs data-id="element-5641">
                    <linearGradient id="colorDay" x1="0" y1="0" x2="0" y2="1" data-id="element-5642">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} data-id="element-5643" />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.2} data-id="element-5644" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-5645" />
                  <XAxis dataKey="label" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} data-id="element-5646" />
                  <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-5647" />
                  <Tooltip content={<CustomTooltip data-id="element-5649" />} cursor={{
                  fill: 'transparent'
                }} data-id="element-5648" />
                  <Bar dataKey="pedidos" name="Pedidos" fill="url(#colorDay)" radius={[4, 4, 0, 0]} data-id="element-5650" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5651">
          {/* Sales by Company Chart */}
          <Card className="p-6 border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-5652">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5653">
              Ventas por Empresa
            </h3>
            <div className="h-[300px] w-full" data-id="element-5654">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5655">
                <BarChart layout="vertical" data={companyChartData} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-5656">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" data-id="element-5657" />
                  <XAxis type="number" hide data-id="element-5658" />
                  <YAxis dataKey="name" type="category" width={100} stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} data-id="element-5659" />
                  <Tooltip content={<CustomTooltip data-id="element-5661" />} data-id="element-5660" />
                  <Bar dataKey="ventas" name="Ventas" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} data-id="element-5662" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Top Products Chart */}
          <Card className="p-6 border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-5663">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5664">
              Productos Más Vendidos
            </h3>
            <div className="h-[300px] w-full" data-id="element-5665">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5666">
                <PieChart data-id="element-5667">
                  <Pie data={topProductsData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} fill="#8884d8" paddingAngle={5} dataKey="value" data-id="element-5668">
                    {topProductsData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-5669" />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip data-id="element-5671" />} data-id="element-5670" />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" formatter={value => <span className="text-xs text-gray-600 dark:text-gray-300 ml-1" data-id="element-5673">
                        {value}
                      </span>} data-id="element-5672" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5674">
        {/* Sales by Company Table (Legacy View) */}
        <Card className="h-full flex flex-col" data-id="element-5675">
          <div className="flex items-center justify-between mb-6" data-id="element-5676">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5677">
              <Building2 className="w-5 h-5 text-gray-400" data-id="element-5678" />
              Detalle por Empresa
            </h3>
          </div>
          <div className="space-y-4 flex-1 overflow-y-auto max-h-[400px] pr-2" data-id="element-5679">
            {salesByCompany.map((sale, index) => <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-5680">
                <div className="flex justify-between items-start mb-2" data-id="element-5681">
                  <div data-id="element-5682">
                    <p className="font-bold text-gray-900 dark:text-white" data-id="element-5683">
                      {sale.company}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5684">
                      {sale.orders} pedidos
                    </p>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5685">
                    RD${' '}
                    {sale.total.toLocaleString('es-DO', {
                  minimumFractionDigits: 2
                })}
                  </p>
                </div>

                {/* Visual bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-2" data-id="element-5686">
                  <div className="bg-blue-600 h-1.5 rounded-full" style={{
                width: `${sale.total / totalSales * 100}%`
              }} data-id="element-5687"></div>
                </div>

                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400" data-id="element-5688">
                  <span data-id="element-5689">
                    Subtotal: RD${' '}
                    {sale.subtotal.toLocaleString('es-DO', {
                  minimumFractionDigits: 2
                })}
                  </span>
                  <span data-id="element-5690">
                    ITBIS: RD${' '}
                    {sale.tax.toLocaleString('es-DO', {
                  minimumFractionDigits: 2
                })}
                  </span>
                </div>
              </div>)}
            {salesByCompany.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400 italic" data-id="element-5691">
                No hay datos para mostrar
              </div>}
          </div>
        </Card>

        {/* Sales by Branch */}
        <Card className="h-full flex flex-col" data-id="element-5692">
          <div className="flex items-center justify-between mb-6" data-id="element-5693">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5694">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-5695" />
              Ventas por Sucursal
            </h3>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto flex-1" data-id="element-5696">
            <table className="w-full text-left border-collapse" data-id="element-5697">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0" data-id="element-5698">
                <tr data-id="element-5699">
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5700">
                    Sucursal
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right" data-id="element-5701">
                    Pedidos
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right" data-id="element-5702">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5703">
                {branchSales.map((sale: any, index: number) => <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-5704">
                    <td className="px-4 py-3" data-id="element-5705">
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5706">
                        {sale.branch}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5707">
                        {sale.company}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right font-mono" data-id="element-5708">
                      {sale.orders}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right" data-id="element-5709">
                      RD${' '}
                      {sale.total.toLocaleString('es-DO', {
                    minimumFractionDigits: 2
                  })}
                    </td>
                  </tr>)}
                {branchSales.length === 0 && <tr data-id="element-5710">
                    <td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic" data-id="element-5711">
                      No hay datos para mostrar
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3 flex-1 overflow-y-auto max-h-[400px]" data-id="element-5712">
            {branchSales.map((sale: any, index: number) => <div key={index} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-5713">
                <div className="flex justify-between items-start mb-2" data-id="element-5714">
                  <div data-id="element-5715">
                    <p className="font-bold text-gray-900 dark:text-white" data-id="element-5716">
                      {sale.branch}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5717">
                      {sale.company}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-5718">
                    <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5719">
                      RD${' '}
                      {sale.total.toLocaleString('es-DO', {
                    minimumFractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono" data-id="element-5720">
                      {sale.orders} pedidos
                    </p>
                  </div>
                </div>

                {/* Mini bar chart visual */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 mt-2" data-id="element-5721">
                  <div className="bg-purple-600 h-1 rounded-full" style={{
                width: `${Math.min(sale.total / (branchSales[0]?.total || 1) * 100, 100)}%`
              }} data-id="element-5722"></div>
                </div>
              </div>)}
            {branchSales.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400 italic" data-id="element-5723">
                No hay datos para mostrar
              </div>}
          </div>
        </Card>
      </div>
    </div>;
}